import React, { useEffect, useState } from "react";

import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import { UserFormValues, userSchema } from "../../schemas/formSchema";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { AppButton } from "../ui/AppButton";
import { FormField2 } from "../form/FormField2";
import CustomPhoneInput from "../ui/phone-input";
import { useOrganizationsList } from "../../api/organization";
import { useUnionCooperativeList } from "../../api/admin";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { message } from "antd";
import { InputPriceEnum } from "../../enums/InputPrice.enum";

type CreateUserModalProps = {
  open: boolean;
  selectedId?: string;
  hideModal: () => void;
  isCreatingUser?: boolean;
  trigger?: React.ReactNode;
  mode?: "create" | "edit";
  onSubmit: (values: UserFormValues) => Promise<void> | void;
  onOpenChange: (open: boolean) => void;
  defaultValues?: Partial<UserFormValues>;
};

const CreateUserModal: React.FC<CreateUserModalProps> = ({
  mode = "create",
  open,
  onOpenChange,
  onSubmit,
  trigger,
  isCreatingUser
 
}) => {
  const { user } = useAuth() as AuthContextValue;
  const ownerRoles = ["WEB_SHEGAFRE_ADMIN"];
  const agroDealerRoles = [
    "WEB_AGRO_DEALER_OPERATION",
    "WEB_AGRO_DEALER_ADMIN",
    "MOB_AGRO_DEALER_STORE",
  ];
  const bankRoles = ["WEB_BANK_OPERATION", "WEB_BANK_ADMIN"];
  const insuranceRoles = ["INSURANCE_ADMIN", "INSURANCE_USER"];
  const offTakerRoles = ["WEB_OFF_TAKER_OPERATION", "WEB_OFF_TAKER_ADMIN"];
  const shegaFreRoles = [
    "WEB_SHEGAFRE_OPERATION",
    "MOB_VALUE_CHAIN_EXECUTES",
    "MOB_WAREHOUSE_PERSON",
    "MOB_SELLS_PERSON",
    "MOB_AGRONOMY_PERSON",
  ];



  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    register,
    watch,
    reset,
  } = useForm<UserFormValues>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      fullName: "",
      userName: "",
      email: "",
      phoneNumber: "",
      role: "",
      selectedUnionId: "",
      selectedCooperativeId: "",
    },
  });

  
  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const title = mode === "create" ? "Create New User" : "Edit User";
  const subTitle =
    mode === "create"
      ? "Add a new user to the system."
      : "Update user information.";

  const [options, setOptions] = useState<string[]>([]);

  useEffect(() => {
    if (user) {
      switch (user.roleName) {
        case "ROLE_SUPER_ADMIN":
          setOptions(ownerRoles);
          break;
        case "ROLE_MPC_ADMIN":
          setOptions(agroDealerRoles);
          break;
        case "ROLE_MFI_ADMIN":
          setOptions(bankRoles);
          break;
        case "INSURANCE_ADMIN":
          setOptions(insuranceRoles);
          break;
        case "ROLE_OFF_TAKER_ADMIN":
          setOptions(offTakerRoles);
          break;
        case "ROLE_KFT_SUPER_ADMIN":
          setOptions(shegaFreRoles);
          break;
        default:
          break;
      }
    }
  }, [user]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-5xl bg-white p-10 !rounded-[30px] z-50">
        <form onSubmit={handleSubmit(onSubmit)} className="">
          <DialogHeader className="flex flex-row items-center justify-between border-b border-gray-200 pb-6">
            {/* Left side: Title and subtitle */}
            <div>
              <DialogTitle className="text-xl font-semibold text-[#09090B]">
                {title}
              </DialogTitle>
              <p className="text-sm text-[#505050] pt-2">{subTitle}</p>
            </div>

            {/* Right side: Buttons */}
            <div className="flex items-center gap-2">
              <AppButton
                type="button"
                variant="outline"
                className="px-6"
                onClick={() => onOpenChange(false)} // Use onOpenChange directly
              >
                Cancel
              </AppButton>
              <AppButton
                variant="success"
                type="submit"
                className="px-6 font-medium"
                isLoading={isCreatingUser}
              >
                {mode === "create" ? "Create" : "Update"}
              </AppButton>
            </div>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div className="pt-6 !mt-0">
                <FormField2
                  label="Role"
                  name="role"
                  as="select"
                  placeholder="Select Role"
                  options={options.map((role) => ({
                    label: role,
                    value: role,
                  }))}
                  value={watch("role")}
                  onChange={(val) => setValue("role", val)}
                  error={errors.role}
                  required
                />

                <FormField2
                  label="Full Name"
                  name="fullName"
                  placeholder="Enter email address"
                  register={register("fullName")}
                  error={errors.fullName}
                  required
                  className="pt-4"
                />
              </div>

              <div className="space-y-4">
                <div className="!mt-0 ">
                  <FormField2
                    label="Email"
                    name="email"
                    placeholder="Enter email address"
                    register={register("email")}
                    error={errors.email}
                    required
                  />

                  <FormField2
                    label="Bank Account"
                    name="bankAccount"
                    placeholder="Enter bank account"
                    register={register("bankAccount")}
                    error={errors.email}
                    className="pt-4"
                  />
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div className="pt-6 !mt-0">
                <FormField2
                  label="Username"
                  name="userName"
                  placeholder="Enter username"
                  register={register("userName")}
                  error={errors.userName}
                  required
                  className="pb-4"
                />

                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field, fieldState }) => (
                    <CustomPhoneInput
                      label="Primary Phone Number"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>

            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserModal;
